// Tenantra Inc. - Admin: Careers (job posts + hiring pipeline)
import { useEffect, useState } from 'react';
import { Loader2, Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import api, { apiHelpers } from '@/services/api';
import { formatDate } from '@/utils/date';
import { StageDot, STAGE_LABELS } from '@/pages/careers/CareersLayout';
import { REGIONS, ADMIN_FILTER_COUNTRIES } from '@/data/regions';

const STAGES = ['submitted', 'in_review', 'interview', 'offer', 'hired', 'rejected'];
const field = 'h-8 text-xs rounded-none border-slate-300';

export const CareersPanel = () => {
  const [tab, setTab] = useState('applications');
  const [jobs, setJobs] = useState([]);
  const [apps, setApps] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [filterJob, setFilterJob] = useState('all');
  const [filterStage, setFilterStage] = useState('all');
  const [filterCountry, setFilterCountry] = useState('all');
  const [filterState, setFilterState] = useState('all');
  const [selected, setSelected] = useState(null);
  const [editingJob, setEditingJob] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterJob !== 'all') params.set('job_id', filterJob);
      if (filterStage !== 'all') params.set('stage', filterStage);
      if (filterCountry !== 'all') params.set('country', filterCountry);
      if (filterCountry !== 'all' && filterState !== 'all') params.set('state_of_origin', filterState);
      const [j, a] = await Promise.all([api.get('/careers/admin/jobs'), api.get(`/careers/admin/applications?${params}`)]);
      setJobs(j.data.jobs);
      setApps(a.data.applications);
      setCounts(a.data.counts);
    } catch (err) {
      toast.error(apiHelpers.handleError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filterJob, filterStage, filterCountry, filterState]);

  return (
    <div data-testid="admin-careers-panel">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold text-slate-900">Careers</h1>
          <p className="text-slate-500 text-[11px]">Job posts and applicant pipeline</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex border border-slate-200">
            {['applications', 'jobs'].map(t => (
              <button key={t} onClick={() => setTab(t)} className={`px-3 h-8 text-xs capitalize ${tab === t ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50'}`} data-testid={`careers-tab-${t}`}>
                {t}
              </button>
            ))}
          </div>
          {tab === 'jobs' && (
            <Button size="sm" onClick={() => setEditingJob({})} className="h-8 text-xs bg-teal-600 hover:bg-teal-700 text-white" data-testid="careers-new-job-btn">
              <Plus className="w-3.5 h-3.5 mr-1" />New post
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-3 sm:grid-cols-6 gap-px bg-slate-200 border border-slate-200 mb-4" data-testid="careers-stage-counts">
        {STAGES.map(s => (
          <button key={s} onClick={() => setFilterStage(filterStage === s ? 'all' : s)} className={`bg-white px-3 py-2 text-left ${filterStage === s ? 'ring-1 ring-inset ring-teal-600' : 'hover:bg-slate-50'}`} data-testid={`stage-count-${s}`}>
            <p className="text-[10px] uppercase tracking-wide text-slate-500">{STAGE_LABELS[s]}</p>
            <p className="text-base font-semibold text-slate-900">{counts[s] ?? 0}</p>
          </button>
        ))}
      </div>

      {tab === 'applications' ? (
        <>
          <div className="flex items-center gap-2 mb-3">
            <Select value={filterJob} onValueChange={setFilterJob}>
              <SelectTrigger className="h-8 text-xs w-64 rounded-none" data-testid="careers-filter-job"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">All positions</SelectItem>
                {jobs.map(j => <SelectItem key={j.id} value={j.id}>{j.title}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterCountry} onValueChange={(v) => { setFilterCountry(v); setFilterState('all'); }}>
              <SelectTrigger className="h-8 text-xs w-40 rounded-none" data-testid="careers-filter-country"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">All countries</SelectItem>
                {ADMIN_FILTER_COUNTRIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            {filterCountry !== 'all' && (
              <Select value={filterState} onValueChange={setFilterState}>
                <SelectTrigger className="h-8 text-xs w-56 rounded-none" data-testid="careers-filter-state"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white max-h-72">
                  <SelectItem value="all">All {filterCountry === 'Canada' ? 'provinces' : 'states'}</SelectItem>
                  {REGIONS[filterCountry].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            {filterStage !== 'all' && <button onClick={() => setFilterStage('all')} className="text-xs text-slate-500 hover:text-slate-800">Clear stage filter</button>}
          </div>
          <ApplicationsTable apps={apps} loading={loading} onOpen={async (a) => { const r = await api.get(`/careers/admin/applications/${a.id}`); setSelected(r.data); }} />
        </>
      ) : (
        <JobsTable jobs={jobs} loading={loading} onEdit={setEditingJob} />
      )}

      {selected && <ApplicationDrawer app={selected} onClose={() => setSelected(null)} onChanged={async () => { const r = await api.get(`/careers/admin/applications/${selected.id}`); setSelected(r.data); load(); }} />}
      {editingJob && <JobEditor job={editingJob} onClose={() => setEditingJob(null)} onSaved={() => { setEditingJob(null); load(); }} />}
    </div>
  );
};

const ApplicationsTable = ({ apps, loading, onOpen }) => (
  <div className="border border-slate-200 bg-white overflow-x-auto" data-testid="careers-applications-table">
    <table className="w-full text-xs">
      <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wide">
        <tr>
          <th className="text-left px-3 py-2 font-medium">Candidate</th>
          <th className="text-left px-3 py-2 font-medium">Position</th>
          <th className="text-left px-3 py-2 font-medium">Origin</th>
          <th className="text-left px-3 py-2 font-medium">Stage</th>
          <th className="text-left px-3 py-2 font-medium">Submitted</th>
          <th className="text-left px-3 py-2 font-medium">Links</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {loading ? (
          <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-400"><Loader2 className="w-4 h-4 animate-spin inline" /></td></tr>
        ) : apps.length === 0 ? (
          <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-500" data-testid="careers-no-applications">No applications</td></tr>
        ) : apps.map(a => (
          <tr key={a.id} onClick={() => onOpen(a)} className="hover:bg-slate-50 cursor-pointer" data-testid={`application-row-${a.id}`}>
            <td className="px-3 py-2">
              <p className="font-medium text-slate-900">{a.full_name}</p>
              <p className="text-slate-500">{a.email}</p>
            </td>
            <td className="px-3 py-2 text-slate-700">{a.job_title}</td>
            <td className="px-3 py-2 text-slate-700" data-testid={`application-origin-${a.id}`}>{a.country ? <>{a.state_of_origin}<span className="block text-slate-500">{a.country}</span></> : <span className="text-slate-400">Not provided</span>}</td>
            <td className="px-3 py-2"><StageDot stage={a.stage} /></td>
            <td className="px-3 py-2 text-slate-500">{formatDate(a.created_at)}</td>
            <td className="px-3 py-2 text-slate-500">{[a.linkedin_url && 'LinkedIn', a.portfolio_url && 'Portfolio'].filter(Boolean).join(' · ') || 'Not provided'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const JobsTable = ({ jobs, loading, onEdit }) => (
  <div className="border border-slate-200 bg-white overflow-x-auto" data-testid="careers-jobs-table">
    <table className="w-full text-xs">
      <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wide">
        <tr>
          <th className="text-left px-3 py-2 font-medium">Title</th>
          <th className="text-left px-3 py-2 font-medium">Location</th>
          <th className="text-left px-3 py-2 font-medium">Type</th>
          <th className="text-left px-3 py-2 font-medium">Status</th>
          <th className="text-right px-3 py-2 font-medium">Applicants</th>
          <th className="px-3 py-2"></th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {loading ? (
          <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-400"><Loader2 className="w-4 h-4 animate-spin inline" /></td></tr>
        ) : jobs.map(j => (
          <tr key={j.id} className="hover:bg-slate-50" data-testid={`job-row-${j.id}`}>
            <td className="px-3 py-2 font-medium text-slate-900">{j.title}<span className="block text-slate-400 font-normal">/careers/{j.slug}</span></td>
            <td className="px-3 py-2 text-slate-700">{j.location}</td>
            <td className="px-3 py-2 text-slate-700">{j.employment_type}</td>
            <td className="px-3 py-2 capitalize text-slate-700">{j.status}</td>
            <td className="px-3 py-2 text-right text-slate-700">{j.application_count}</td>
            <td className="px-3 py-2 text-right">
              <button onClick={() => onEdit(j)} className="text-teal-700 hover:underline" data-testid={`job-edit-${j.id}`}>Edit</button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const ApplicationDrawer = ({ app, onClose, onChanged }) => {
  const [stage, setStage] = useState(app.stage);
  const [note, setNote] = useState('');
  const [internalNote, setInternalNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [showInterview, setShowInterview] = useState(false);
  const [iv, setIv] = useState({ date: '', time: '', duration_minutes: '30', meeting_link: '', message: '' });

  const openResume = async () => {
    const r = await api.get(`/careers/admin/applications/${app.id}/resume`, { responseType: 'blob' });
    window.open(URL.createObjectURL(r.data), '_blank', 'noopener');
  };

  const saveStage = async () => {
    try {
      setSaving(true);
      const r = await api.put(`/careers/admin/applications/${app.id}/stage`, { stage, note: note || undefined, internal_note: internalNote || undefined });
      toast.success(r.data.emailed ? 'Stage updated and candidate emailed' : 'Stage updated');
      setNote(''); setInternalNote('');
      onChanged();
    } catch (err) { toast.error(apiHelpers.handleError(err)); } finally { setSaving(false); }
  };

  const schedule = async () => {
    if (!iv.date || !iv.time || !iv.meeting_link) { toast.error('Date, time and meeting link are required'); return; }
    try {
      setSaving(true);
      const scheduled_at = new Date(`${iv.date}T${iv.time}`).toISOString();
      const r = await api.post(`/careers/admin/applications/${app.id}/interview`, { scheduled_at, duration_minutes: Number(iv.duration_minutes), meeting_link: iv.meeting_link, message: iv.message || undefined });
      toast.success(r.data.emailed ? 'Interview booked and invitation emailed' : 'Interview booked (email not sent)');
      setShowInterview(false);
      setIv({ date: '', time: '', duration_minutes: '30', meeting_link: '', message: '' });
      onChanged();
    } catch (err) { toast.error(apiHelpers.handleError(err)); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/40 z-50 flex justify-end" onClick={onClose}>
      <div className="bg-white w-full max-w-xl h-full overflow-y-auto shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="application-drawer">
        <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between">
          <div>
            <h2 className="text-sm font-bold text-slate-900">{app.full_name}</h2>
            <p className="text-[11px] text-slate-500">{app.job_title} · applied {formatDate(app.created_at)}</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100" data-testid="application-drawer-close"><X className="w-4 h-4 text-slate-500" /></button>
        </div>

        <div className="px-5 py-4 grid grid-cols-2 gap-3 text-xs border-b border-slate-200">
          <Info label="Email" value={<a href={`mailto:${app.email}`} className="text-teal-700 hover:underline">{app.email}</a>} />
          <Info label="Phone" value={app.phone || 'Not provided'} />
          <Info label="Country" value={app.country || 'Not provided'} />
          <Info label="State of origin" value={app.state_of_origin || 'Not provided'} />
          <Info label="LinkedIn" value={app.linkedin_url ? <a href={app.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-teal-700 hover:underline break-all">{app.linkedin_url}</a> : 'Not provided'} />
          <Info label="Portfolio" value={app.portfolio_url ? <a href={app.portfolio_url} target="_blank" rel="noopener noreferrer" className="text-teal-700 hover:underline break-all">{app.portfolio_url}</a> : 'Not provided'} />
          <Info label="Resume" value={<button onClick={openResume} className="text-teal-700 hover:underline" data-testid="application-view-resume">{app.resume_filename}</button>} />
          <Info label="Current stage" value={<StageDot stage={app.stage} />} />
        </div>

        <div className="px-5 py-4 border-b border-slate-200">
          <p className="text-[10px] uppercase tracking-wide text-slate-500 mb-2">Cover letter</p>
          <p className="text-xs text-slate-700 whitespace-pre-wrap leading-relaxed" data-testid="application-cover-letter">{app.cover_letter}</p>
        </div>

        <div className="px-5 py-4 border-b border-slate-200 space-y-3">
          <p className="text-[10px] uppercase tracking-wide text-slate-500">Move stage</p>
          <Select value={stage} onValueChange={setStage}>
            <SelectTrigger className="h-8 text-xs rounded-none" data-testid="application-stage-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">{STAGES.map(s => <SelectItem key={s} value={s}>{STAGE_LABELS[s]}</SelectItem>)}</SelectContent>
          </Select>
          <div>
            <Label className="text-[10px] text-slate-600">Note to candidate (optional{['offer', 'hired', 'rejected'].includes(stage) ? ', included in email' : ''})</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="text-xs rounded-none border-slate-300" data-testid="application-stage-note" />
          </div>
          <div>
            <Label className="text-[10px] text-slate-600">Internal note (never shown to candidate)</Label>
            <Textarea value={internalNote} onChange={(e) => setInternalNote(e.target.value)} rows={2} className="text-xs rounded-none border-slate-300" data-testid="application-internal-note" />
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={saveStage} disabled={saving} className="h-8 text-xs bg-slate-900 hover:bg-slate-800 text-white" data-testid="application-save-stage">{saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Update stage'}</Button>
            <Button size="sm" variant="outline" onClick={() => setShowInterview(!showInterview)} className="h-8 text-xs rounded-none" data-testid="application-book-interview-btn">Book interview</Button>
          </div>
          {['offer', 'hired', 'rejected'].includes(stage) && stage !== app.stage && <p className="text-[11px] text-amber-600">The candidate will receive an email for this stage.</p>}
        </div>

        {showInterview && (
          <div className="px-5 py-4 border-b border-slate-200 space-y-3 bg-slate-50" data-testid="interview-form">
            <p className="text-[10px] uppercase tracking-wide text-slate-500">Interview details</p>
            <div className="grid grid-cols-3 gap-2">
              <div><Label className="text-[10px] text-slate-600">Date</Label><Input type="date" value={iv.date} onChange={(e) => setIv({ ...iv, date: e.target.value })} className={field} data-testid="interview-date" /></div>
              <div><Label className="text-[10px] text-slate-600">Time (your local)</Label><Input type="time" value={iv.time} onChange={(e) => setIv({ ...iv, time: e.target.value })} className={field} data-testid="interview-time" /></div>
              <div>
                <Label className="text-[10px] text-slate-600">Duration</Label>
                <Select value={iv.duration_minutes} onValueChange={(v) => setIv({ ...iv, duration_minutes: v })}>
                  <SelectTrigger className={field} data-testid="interview-duration"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{['15', '30', '45', '60', '90'].map(d => <SelectItem key={d} value={d}>{d} min</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label className="text-[10px] text-slate-600">Meeting link (Google Meet, Zoom, Teams)</Label><Input type="url" value={iv.meeting_link} onChange={(e) => setIv({ ...iv, meeting_link: e.target.value })} className={field} data-testid="interview-link" /></div>
            <div><Label className="text-[10px] text-slate-600">Message (optional)</Label><Textarea value={iv.message} onChange={(e) => setIv({ ...iv, message: e.target.value })} rows={2} className="text-xs rounded-none border-slate-300" data-testid="interview-message" /></div>
            <Button size="sm" onClick={schedule} disabled={saving} className="h-8 text-xs bg-teal-600 hover:bg-teal-700 text-white" data-testid="interview-send-btn">{saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Send invitation'}</Button>
          </div>
        )}

        {app.interviews?.length > 0 && (
          <div className="px-5 py-4 border-b border-slate-200">
            <p className="text-[10px] uppercase tracking-wide text-slate-500 mb-2">Interviews</p>
            <ul className="space-y-1.5 text-xs" data-testid="application-interviews">
              {app.interviews.map(i => <li key={i.id} className="text-slate-700">{formatDate(i.scheduled_at, true)} · {i.duration_minutes} min · <a href={i.meeting_link} className="text-teal-700 hover:underline break-all" target="_blank" rel="noopener noreferrer">{i.meeting_link}</a></li>)}
            </ul>
          </div>
        )}

        <div className="px-5 py-4">
          <p className="text-[10px] uppercase tracking-wide text-slate-500 mb-2">History</p>
          <ul className="space-y-2 text-xs" data-testid="application-history">
            {[...(app.timeline || [])].reverse().map((t, i) => (
              <li key={i} className="flex gap-3"><span className="text-slate-400 w-32 flex-shrink-0">{formatDate(t.at, true)}</span><div><span className="text-slate-800 font-medium">{STAGE_LABELS[t.stage]}</span>{t.note && <p className="text-slate-600">{t.note}</p>}</div></li>
            ))}
            {(app.internal_notes || []).map((n, i) => (
              <li key={`n${i}`} className="flex gap-3"><span className="text-slate-400 w-32 flex-shrink-0">{formatDate(n.at, true)}</span><p className="text-slate-500 italic">Internal: {n.text}</p></li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

const Info = ({ label, value }) => (
  <div><p className="text-[10px] uppercase tracking-wide text-slate-500">{label}</p><div className="text-slate-800 mt-0.5">{value}</div></div>
);

const JobEditor = ({ job, onClose, onSaved }) => {
  const [form, setForm] = useState({
    title: job.title || '', location: job.location || 'Remote (Canada)', employment_type: job.employment_type || 'Contract',
    compensation: job.compensation || '', summary: job.summary || '', status: job.status || 'open',
    sections: job.sections?.length ? job.sections.map(s => ({ heading: s.heading, body: s.text || (s.items || []).map(i => `• ${i}`).join('\n') }))
      : [{ heading: 'What you will do', body: '' }, { heading: 'What we need', body: '' }, { heading: 'Nice to have', body: '' }, { heading: 'How we will hire', body: '' }]
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setSection = (i, k, v) => setForm({ ...form, sections: form.sections.map((s, j) => j === i ? { ...s, [k]: v } : s) });

  const save = async () => {
    const sections = form.sections.filter(s => s.heading.trim() && s.body.trim()).map(s => {
      const lines = s.body.split('\n').map(l => l.trim()).filter(Boolean);
      const bullets = lines.filter(l => /^[•\-*]/.test(l));
      return bullets.length === lines.length
        ? { heading: s.heading.trim(), items: lines.map(l => l.replace(/^[•\-*]\s*/, '')) }
        : { heading: s.heading.trim(), text: lines.join(' ') };
    });
    const payload = { ...form, compensation: form.compensation || null, sections };
    try {
      setSaving(true);
      if (job.id) await api.put(`/careers/admin/jobs/${job.id}`, payload); else await api.post('/careers/admin/jobs', payload);
      toast.success(job.id ? 'Post updated' : 'Post created');
      onSaved();
    } catch (err) { toast.error(apiHelpers.handleError(err)); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/40 z-50 flex justify-end" onClick={onClose}>
      <div className="bg-white w-full max-w-2xl h-full overflow-y-auto shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="job-editor">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-sm font-bold text-slate-900">{job.id ? 'Edit post' : 'New post'}</h2>
          <button onClick={onClose} className="p-1 hover:bg-slate-100" data-testid="job-editor-close"><X className="w-4 h-4 text-slate-500" /></button>
        </div>
        <div className="px-5 py-4 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label className="text-[10px] text-slate-600">Title</Label><Input value={form.title} onChange={set('title')} className={field} data-testid="job-title-input" /></div>
            <div><Label className="text-[10px] text-slate-600">Location</Label><Input value={form.location} onChange={set('location')} className={field} data-testid="job-location-input" /></div>
            <div>
              <Label className="text-[10px] text-slate-600">Employment type</Label>
              <Select value={form.employment_type} onValueChange={(v) => setForm({ ...form, employment_type: v })}>
                <SelectTrigger className={field} data-testid="job-type-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{['Contract', 'Full-time', 'Part-time', 'Internship'].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-[10px] text-slate-600">Compensation (optional)</Label><Input value={form.compensation} onChange={set('compensation')} className={field} data-testid="job-compensation-input" /></div>
            <div>
              <Label className="text-[10px] text-slate-600">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className={field} data-testid="job-status-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white"><SelectItem value="draft">Draft</SelectItem><SelectItem value="open">Open</SelectItem><SelectItem value="closed">Closed</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <div><Label className="text-[10px] text-slate-600">Summary (opening paragraph)</Label><Textarea value={form.summary} onChange={set('summary')} rows={4} className="text-xs rounded-none border-slate-300" data-testid="job-summary-input" /></div>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-[10px] uppercase tracking-wide text-slate-500">Sections. One bullet per line starting with "-"; plain lines become a paragraph</p>
              <button onClick={() => setForm({ ...form, sections: [...form.sections, { heading: '', body: '' }] })} className="text-xs text-teal-700 hover:underline" data-testid="job-add-section">Add section</button>
            </div>
            {form.sections.map((s, i) => (
              <div key={i} className="border border-slate-200 p-3 space-y-2" data-testid={`job-section-editor-${i}`}>
                <div className="flex gap-2">
                  <Input value={s.heading} onChange={(e) => setSection(i, 'heading', e.target.value)} placeholder="" className={field} data-testid={`job-section-heading-${i}`} />
                  <button onClick={() => setForm({ ...form, sections: form.sections.filter((_, j) => j !== i) })} className="text-xs text-slate-400 hover:text-red-600 px-2">Remove</button>
                </div>
                <Textarea value={s.body} onChange={(e) => setSection(i, 'body', e.target.value)} rows={5} className="text-xs rounded-none border-slate-300 font-mono" data-testid={`job-section-body-${i}`} />
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button size="sm" variant="outline" onClick={onClose} className="h-8 text-xs rounded-none">Cancel</Button>
            <Button size="sm" onClick={save} disabled={saving || !form.title || form.summary.length < 20} className="h-8 text-xs bg-teal-600 hover:bg-teal-700 text-white" data-testid="job-save-btn">{saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Save post'}</Button>
          </div>
        </div>
      </div>
    </div>
  );
};
